import { actions, elements, inputs } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { MAX_NAME_LENGTH, insightConstants } from '../../constants';
import {
  AccessLevel,
  CollectionListItem,
  CollectionType,
  CollectionsErrorType,
  SelectedCollectionOption,
} from '../../types';
import { useGetCollections } from '../../useCollections';

import './AddCollectionName.css';

const { Select } = inputs;
const { IconButton } = actions;
const { Icon } = elements;

const { LOAD_COLLECTIONS_ERROR } = insightConstants;

const LABELS: Record<CollectionType, string> = {
  [CollectionType.Template]: 'Select template',
  [CollectionType.Collection]: 'Create or select multiple collections',
};

const PLACEHOLDERS: Record<CollectionType, string> = {
  [CollectionType.Template]: 'Type to search for an existing template',
  [CollectionType.Collection]: 'Type to create a new collection or search for existing',
};
export interface AddCollectionNameProps {
  selectedType?: CollectionType;
  selectedCollections: SelectedCollectionOption[];
  setSelectedCollections: (selectedCollections: SelectedCollectionOption[]) => void;
  setValid: (valid: boolean) => void;
  setError: (error: string) => void;
}

export const AddCollectionName: React.FC<AddCollectionNameProps> = ({
  selectedType,
  selectedCollections,
  setSelectedCollections,
  setValid,
  setError,
}) => {
  const [userCollections, setUserCollections] = useState<CollectionListItem[]>([]);
  const { collections, loading, error: loadError } = useGetCollections();
  const [newCollectionName, setNewCollectionName] = useState<string>();

  const hasLoadError = loadError && loadError !== CollectionsErrorType.NO_ERROR;

  useEffect(() => {
    setValid(!hasLoadError && selectedCollections.length > 0);
  }, [setValid, selectedCollections.length, hasLoadError]);

  useEffect(() => {
    if (!loading) {
      if (!hasLoadError) {
        const filteredByType =
          collections?.items.filter(c => c.accessLevel === AccessLevel.Owner && c.type === selectedType) || [];

        setUserCollections(filteredByType);
        setError(undefined);
      } else {
        setUserCollections([]);
        setError(LOAD_COLLECTIONS_ERROR);
      }
    }
  }, [collections, selectedType, hasLoadError, loading, setError]);

  const selectOptions = useMemo(() => {
    return userCollections
      .filter(x => !selectedCollections.find(y => y.id === x.id))
      .map(x => ({
        label: x.name,
        value: x.id,
      }));
  }, [userCollections, selectedCollections]);

  const handleSetName = useCallback(
    (name: string) => {
      setNewCollectionName(name?.trim());
    },
    [setNewCollectionName]
  );

  const handleSelectCollection = useCallback(
    (collectionId: string) => {
      const selectedCollection = userCollections.find(x => x.id === collectionId);
      if (selectedCollection) {
        setSelectedCollections([...selectedCollections, { id: collectionId, name: selectedCollection?.name }]);
      }
    },
    [userCollections, selectedCollections, setSelectedCollections]
  );

  const handleUnselectCollection = useCallback(
    (collection: SelectedCollectionOption) => {
      setSelectedCollections(selectedCollections.filter(x => x !== collection));
    },
    [selectedCollections, setSelectedCollections]
  );

  const handleAddNewCollection = useCallback(() => {
    setSelectedCollections([...selectedCollections, { name: newCollectionName }]);
  }, [selectedCollections, newCollectionName, setSelectedCollections]);

  const newCollectionNameValid = useMemo(() => {
    const duplicatesExistingCollection = selectOptions.some(option => option.label === newCollectionName?.trim());
    const duplicatesSelectedCollection = selectedCollections.some(option => option.name === newCollectionName?.trim());
    return newCollectionName && !duplicatesExistingCollection && !duplicatesSelectedCollection;
  }, [newCollectionName, selectOptions, selectedCollections]);

  const addNewCollectionButtonDisabled = !newCollectionNameValid || hasLoadError;

  const hasCollections = !!userCollections?.length;

  const buttonOptions =
    (hasCollections && !newCollectionName) || selectedType === CollectionType.Template
      ? null
      : {
          icon: (
            <Icon color={addNewCollectionButtonDisabled ? 'inactive' : 'brand'}>
              <Icon.icons.Add />
            </Icon>
          ),
          text: 'Create new collection',
          isDisabled: addNewCollectionButtonDisabled,
          callback: () => handleAddNewCollection(),
        };

  return (
    <div className="add-collection-name-select">
      <Select
        name="collection-select"
        label={LABELS[selectedType]}
        value={null}
        placeholder={loading ? `Loading ${selectedType}s...` : PLACEHOLDERS[selectedType]}
        options={selectOptions}
        onInputChange={handleSetName}
        onChange={handleSelectCollection}
        errorMessage={hasLoadError ? 'Data has not been loaded' : null}
        clearButtonPosition="above"
        hasError={hasLoadError}
        button={buttonOptions}
        maxInputLength={MAX_NAME_LENGTH}
        customErrorOptionMessage={
          !hasCollections && !hasLoadError && selectedType === CollectionType.Collection
            ? "You don't have any collections yet. Start typing to create a new one."
            : 'No results found'
        }
      />
      <ul className="overflow-y-auto">
        {selectedCollections.map((col, idx) => (
          <li className={'hover:bg-light pl-3 flex h-16 items-center overflow-hidden border-b border-light'} key={idx}>
            <div className="flex-grow truncate">
              <div className="text-md white-space-pre truncate">
                {col.name?.trim()}
                {!col.id && <span className="text-inactive"> (new)</span>}
              </div>
            </div>
            <div className="py-5 px-3">
              <IconButton color="grey" type="button" onClick={() => handleUnselectCollection(col)}>
                <Icon.icons.Close />
              </IconButton>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
